import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { apiRequest } from '../../services/apiClient';
import { getCurrentUser } from '../../api/authService';
import { extractErrorMessage } from '../../utils/errorHandler';
import { excludeSuperAdminUsers } from '../../utils/reportFilters';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import CountyFilterSelect, { countyFilterToId, regionFilterToId } from '../../components/reports/CountyFilterSelect';
import { 
  FileText, 
  Search, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  LogIn,
  LogOut,
  Clock,
  Eye,
  Calendar,
  Download,
  ChevronDown
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadStyledXlsx } from '../../utils/exportStyledXlsx';

const parseEmployeeHistoryPage = (response) => {
  const payload = response?.data?.data ?? response?.data ?? response;
  const content = Array.isArray(payload?.content)
    ? payload.content
    : Array.isArray(payload)
      ? payload
      : [];
  return {
    content: excludeSuperAdminUsers(content),
    totalElements: Number(payload?.totalElements ?? content.length),
    totalPages: Number(payload?.totalPages ?? 1),
    pageNumber: Number(payload?.pageNumber ?? 0),
  };
};

const buildEmployeeHistoryQueryParams = (overrides = {}) => {
  const user = getCurrentUser();
  const exportAll = Boolean(overrides.exportAll);
  const params = { ...overrides };

  if (exportAll) {
    params.exportAll = true;
    delete params.page;
    delete params.size;
  } else {
    params.page = overrides.page ?? 0;
    params.size = overrides.size ?? 10;
    delete params.exportAll;
  }

  if (user) {
    const isTeamLead = user.roleCode === 'TEAM_LEAD' || user.role === 'team-lead';
    if (isTeamLead) {
      params.role = 'TEAM_LEAD';
      if (user.teamId != null) {
        params.team_id = user.teamId;
      }
    }
  }
  const regionId = regionFilterToId(overrides.region);
  if (regionId !== null) {
    params.regionId = regionId;
  }
  const countyId = countyFilterToId(overrides.county);
  if (countyId !== null) {
    params.countyId = countyId;
  }
  if (!params.search) {
    delete params.search;
  }
  delete params.region;
  delete params.county;
  return params;
};

const EmployeeHistory = () => {
  const [displayedHistory, setDisplayedHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const searchDebounceRef = useRef(null);

  const loadEmployeeHistory = useCallback(async (page = 0) => {
    try {
      setLoading(true);
      setError('');

      const trimmedSearch = searchTerm.trim();
      const response = await apiRequest('/gateway/registration/UserSignup/employee-history', {
        method: 'GET',
        params: buildEmployeeHistoryQueryParams({
          page,
          size: pageSize,
          search: trimmedSearch || undefined,
          region: selectedRegion,
          county: selectedCounty,
        }),
      });

      const parsed = parseEmployeeHistoryPage(response);
      setDisplayedHistory(parsed.content);
      setTotalRecords(parsed.totalElements);
      setTotalPages(Math.max(parsed.totalPages, 1));
      setCurrentPage(parsed.pageNumber);
    } catch (err) {
      console.error('Error loading employee history:', err);
      const message = extractErrorMessage(err, 'Failed to load employee history');
      setError(message);
      toast.error(message);
      setDisplayedHistory([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [pageSize, searchTerm, selectedRegion, selectedCounty]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    const delay = searchTerm.trim() ? 400 : 0;
    searchDebounceRef.current = setTimeout(() => {
      loadEmployeeHistory(currentPage);
    }, delay);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [currentPage, searchTerm, selectedRegion, selectedCounty, loadEmployeeHistory]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedRegion('all');
    setSelectedCounty('all');
    setCurrentPage(0);
  };

  const hasActiveFilters =
    searchTerm.trim() !== '' || selectedRegion !== 'all' || selectedCounty !== 'all';


  const handleViewDetails = async (item) => {
    try {
      setSelectedUser(item);
      setShowDetailsModal(true);
      setLoadingDetails(true);
      
      // Get userId from the item - check multiple possible field names
      // The backend query includes: u.user_id AS userId
      const userId = item.user_id;
      
      //console.log('🔍 View Details - Item:', item);
      //console.log('🔍 View Details - Extracted userId:', userId);
      
      if (!userId) {
        toast.error('User ID not found. Cannot fetch user details.');
        setLoadingDetails(false);
        setShowDetailsModal(false);
        return;
      }

      // Call API to get user-specific history
      // Backend endpoint: /employee-history/{userId} with @RequestParam("userId")
      // Since backend uses @RequestParam, we pass userId as query parameter
      // The path variable might be ignored, but we include it for completeness
      const apiUrl = `/gateway/registration/UserSignup/employee-history/${userId}`;
      //console.log('📡 Calling API:', apiUrl, 'with userId query param:', userId);
      
      const response = await apiRequest(apiUrl, {
        method: 'GET',
        params: { userId: userId.toString() }
      });
      
      //console.log('📥 API Response:', response);
      
      // Handle different response structures
      let detailsData = [];
      if (Array.isArray(response)) {
        detailsData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        detailsData = response.data;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        detailsData = response.data.data;
      } else if (response?.content && Array.isArray(response.content)) {
        detailsData = response.content;
      }
      
      //console.log('📊 Parsed detailsData:', detailsData);
      
      // Sort by date (most recent first)
      // Backend returns: in_time, out_time, work_date, duration
      detailsData.sort((a, b) => {
        const dateA = new Date(a.work_date || a.workDate || a.in_time || a.inTime || 0);
        const dateB = new Date(b.work_date || b.workDate || b.in_time || b.inTime || 0);
        return dateB - dateA;
      });
      
      setUserDetails(detailsData);
      //console.log('✅ User details loaded:', detailsData.length, 'records');
    } catch (error) {
      console.error('❌ Error loading user details:', error);
      toast.error(extractErrorMessage(error, 'Failed to load user details'));
      setUserDetails([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedUser(null);
    setUserDetails([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
  
    try {
      const date = new Date(dateString);
  
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
  
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
  
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };
  

  

  const formatDuration = (loginTime, logoutTime) => {
    if (!loginTime || !logoutTime) return 'No Duration';
    try {
      const login = new Date(loginTime);
      const logout = new Date(logoutTime);
      const diffMs = logout - login;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${diffHours}h ${diffMinutes}m`;
    } catch (error) {
      return '';
    }
  };

  const fetchEmployeeHistoryForExport = async () => {
    const trimmedSearch = searchTerm.trim();
    const response = await apiRequest('/gateway/registration/UserSignup/employee-history', {
      method: 'GET',
      params: buildEmployeeHistoryQueryParams({
        search: trimmedSearch || undefined,
        region: selectedRegion,
        county: selectedCounty,
        exportAll: true,
      }),
    });
    return parseEmployeeHistoryPage(response).content;
  };

  const mapItemToExportRow = (item, index) => {
    const loginTime = item.inTime || item.intime || item.in_time;
    const logoutTime = item.outTime || item.outtime || item.out_time;
    const email = item.email || item.emailId || item.email_id || '';
    const fullName = item?.username || '';
    const duration = item?.duration || formatDuration(loginTime, logoutTime);
    const isActive = logoutTime ? 'Logged Out' : loginTime ? 'Active Session' : 'Not logged In';
    return {
      sno: index + 1,
      userName: fullName,
      email,
      loginTime: loginTime || 'Not logged In',
      logoutTime: logoutTime || (loginTime ? 'Active Session' : 'Not logged In'),
      duration: duration || 'No Duration',
      status: isActive
    };
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rawData = await fetchEmployeeHistoryForExport();
      if (rawData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const data = rawData.map((item, index) => mapItemToExportRow(item, index));
      const headers = ['S.NO', 'User Name', 'Email', 'Login Time', 'Logout Time', 'Duration', 'Status'];
      const rows = data.map((r) => [
        r.sno,
        r.userName,
        r.email,
        r.loginTime,
        r.logoutTime,
        r.duration,
        r.status,
      ]);
      downloadStyledXlsx({
        fileName: `user-login-report-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'Employee History',
        headers,
        rows,
        numberColumns: [0], // S.NO — plain number, never a date
        textColumns: [1, 2, 5, 6], // User Name, Email, Duration, Status
        dateColumns: [3, 4], // Login Time, Logout Time — only real timestamps
        centerColumns: [0, 3, 4, 5, 6],
      });
      toast.success('Report exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rawData = await fetchEmployeeHistoryForExport();
      if (rawData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const data = rawData.map((item, index) => mapItemToExportRow(item, index));
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('User Login Report', 14, 15);
      doc.setFontSize(10);
      autoTable(doc, {
        startY: 22,
        head: [['S.NO', 'User Name', 'Email', 'Login Time', 'Logout Time', 'Duration', 'Status']],
        body: data.map((r) => [r.sno, r.userName, r.email, r.loginTime, r.logoutTime, r.duration, r.status]),
        styles: { fontSize: 8 }
      });
      doc.save(`user-login-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Table Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between mb-3">
                <span>User Login Details</span>
                <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                  {loading ? 'Loading...' : `${totalRecords} records`}
                </span>
              </CardTitle>
              {/* One-line toolbar: search grows left; filters/actions pack right; Clear last */}
              <div className="flex flex-nowrap items-center gap-2 mt-2 w-full">
                <div className="relative min-w-[18rem] flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by username, email, or name..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10 h-9 w-full"
                  />
                </div>
                <CountyFilterSelect
                  variant="button"
                  className="shrink-0"
                  regionValue={selectedRegion}
                  countyValue={selectedCounty}
                  onRegionChange={(value) => {
                    setSelectedRegion(value);
                    setCurrentPage(0);
                  }}
                  onCountyChange={(value) => {
                    setSelectedCounty(value);
                    setCurrentPage(0);
                  }}
                />
                <div className="relative shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExportMenuOpen((v) => !v)}
                    disabled={loading || exporting}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {exporting ? 'Exporting…' : 'Export'}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                  {exportMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        onClick={handleExportExcel}
                      >
                        <FileText className="h-4 w-4" />
                        Export Excel
                      </button>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                        onClick={handleExportPdf}
                      >
                        <FileText className="h-4 w-4" />
                        Export PDF
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadEmployeeHistory(currentPage)}
                  disabled={loading}
                  className="shrink-0"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  disabled={loading || !hasActiveFilters}
                  className="shrink-0"
                >
                  Clear Filters
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading && displayedHistory.length === 0 ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B2530] mx-auto"></div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Loading employee history...</p>
                </div>
              ) : displayedHistory.length === 0 ? (
                <div className="text-center py-12">
                  <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {searchTerm ? 'No records found matching your search.' : 'No employee history available.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            S.NO
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            User Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Login Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Logout Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Duration
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {displayedHistory.map((item, index) => {
                          const serialNumber = (currentPage * pageSize) + index + 1;
                          // Backend returns: inTime, outTime (camelCase from main query)
                          const loginTime = item.inTime || item.intime || item.in_time;
                          const logoutTime = item.outTime || item.outtime || item.out_time;
                          const email = item.email || item.emailId || item.email_id || '';
                          const fullName = item?.username;
                          const duration = item?.duration;
                          const isActive = logoutTime ? 'Logged Out' : loginTime ? 'Active Session' : 'Not logged In';

                          return (
                            <tr 
                              key={index}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
                                {serialNumber}
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {fullName}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {<div>{email}</div>}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                                  <LogIn className="h-4 w-4 mr-2 text-green-500" />
                                  {loginTime ? loginTime : 'Not logged In'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                                  <LogOut className="h-4 w-4 mr-2 text-red-500" />
                                  {logoutTime ? logoutTime : loginTime?'Active Session':'Not logged In'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                  <Clock className="h-4 w-4 mr-2 text-blue-500" />
                                  {duration ? duration : 'No Duration'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  isActive 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                }`}>
                                  {isActive}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewDetails(item)}
                                  className="flex items-center gap-2"
                                >
                                  <Eye className="h-4 w-4" />
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalRecords > 0 && (
                    <div className="mt-6 flex items-center justify-between">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing <span className="font-medium">{(currentPage * pageSize) + 1}</span> to{' '}
                        <span className="font-medium">{Math.min((currentPage + 1) * pageSize, totalRecords)}</span> of{' '}
                        <span className="font-medium">{totalRecords}</span> records
                        {' '}(Page {currentPage + 1} of {totalPages})
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 0 || loading}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i;
                            } else if (currentPage < 3) {
                              pageNum = i;
                            } else if (currentPage > totalPages - 4) {
                              pageNum = totalPages - 5 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {pageNum + 1}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages - 1 || loading}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* User Details Modal */}
          <Modal
            isOpen={showDetailsModal}
            onClose={handleCloseDetailsModal}
            title={`User Details - ${selectedUser?.username || selectedUser?.email || 'User'}`}
            size="xl"
          >
            {loadingDetails ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B2530] mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">Loading user details...</p>
              </div>
            ) : userDetails.length === 0 ? (
              <div className="text-center py-12">
                <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No login history found for this user.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* User Info */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Username</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser?.username || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {selectedUser?.email || selectedUser?.emailId || 'N/A'}
                      </p>
                    </div>
                    {selectedUser?.roleName && (
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Role</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {selectedUser.roleName}
                        </p>
                      </div>
                    )}
                    {selectedUser?.teamName && (
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Team</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {selectedUser.teamName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Date Range Info */}
                {userDetails.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <Calendar className="h-4 w-4" />
                   
                  </div>
                )}

                {/* History Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Login Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Logout Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Duration
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {userDetails.map((detail, index) => {
                        // Backend returns: in_time, out_time, work_date, duration
                        const workDate = detail.work_date || detail.workDate || detail.workDate;
                        const inTime = detail.in_time || detail.inTime || detail.intime;
                        const outTime = detail.out_time || detail.outTime || detail.outtime;
                        const duration = detail.duration || '';

                        return (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {workDate?formatDate(workDate):''}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                                <LogIn className="h-4 w-4 mr-2 text-green-500" />
                                {inTime }
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-900 dark:text-gray-100">
                                <LogOut className="h-4 w-4 mr-2 text-red-500" />
                                {outTime?outTime : inTime?'Active Session':'Not logged In'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <Clock className="h-4 w-4 mr-2 text-blue-500" />
                                {duration?duration:outTime?inTime?'Active Session':'Not logged In':'No Duration'}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Modal>
    </div>
  );
};

export default EmployeeHistory;

